import { useState } from "react";
import { Zap } from "lucide-react";
import { smoothPath, type Pt } from "../lib/curve";
import { useSize } from "../lib/useSize";

/**
 * Scenarios on the same chart as the baseline (PRD §8). The curves and the table are the worked example's
 * illustrative outputs (§14.1), written into the page; picking a row only chooses which one to draw.
 */

/** Month offsets from Sep 2026 of the points each curve is drawn through. */
const AT = [0, 3, 9, 15, 21, 27];
const SPAN = 27;
/** Dec 2027, the date the table reports. */
const REPORT = 3;

type Scenario = { id: string; name: string; how: "Assume" | "Make it so" | null; curve: number[]; cut: number };
const SCENARIOS: Scenario[] = [
	{ id: "baseline", name: "Baseline", how: null, curve: [0, 4, 14, 29, 38, 45], cut: 12 },
	{ id: "stays", name: "Acme stays, no AI crash", how: "Assume", curve: [0, 1, 2, 4, 6, 8], cut: 5 },
	{ id: "exits", name: "Acme exits", how: "Assume", curve: [0, 22, 81, 96, 97, 98], cut: 13 },
	{ id: "downturn", name: "Acme exit in a downturn", how: "Assume", curve: [0, 30, 88, 98, 99, 99], cut: 16 },
	{ id: "cost", name: "Cost per head $170k", how: "Make it so", curve: [0, 8, 22, 41, 50, 57], cut: 13 },
];

export function Scenarios() {
	const [pick, setPick] = useState("exits");
	const s = SCENARIOS.find((x) => x.id === pick)!;
	const base = SCENARIOS[0];
	const inScenario = s.id !== "baseline";

	return (
		<section id="scenarios" className="mx-auto max-w-[1240px] px-5 py-24 md:px-8 lg:py-32">
			<div className="grid gap-x-16 gap-y-6 lg:grid-cols-12">
				<h2 className="text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em] lg:col-span-6">Ask what if. Keep the baseline in view.</h2>
				<div className="space-y-4 lg:col-span-5 lg:col-start-8 lg:pt-2">
					<p>
						A scenario is a named what-if across the whole worldview. Give any event or metric its own values, the same way you set its baseline, and everything
						downstream follows. The baseline stays on the chart, so the difference is never out of sight.
					</p>
					<p className="text-quiet">
						<em className="voice text-[1.08em] text-eggplant">Assume</em> Acme leaves, and the model also asks what else that implies: a recession, say, becomes
						more likely. <em className="voice text-[1.08em] text-eggplant">Make it so</em>, and only Acme changes.
					</p>
				</div>
			</div>

			<div className="mt-14 grid overflow-hidden rounded-md border border-line bg-white lg:grid-cols-12">
				<div className="border-b border-line p-5 sm:p-7 lg:col-span-7 lg:border-r lg:border-b-0">
					<div className="flex items-center gap-1.5 text-sm font-medium text-ink">
						<Zap size={15} strokeWidth={2.2} className="text-quiet" aria-hidden /> Layoffs needed
					</div>
					<p className="voice mt-0.5 text-[17px] text-eggplant">Will the firm need to cut 10% or more of its staff?</p>
					<div className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-3" aria-live="polite">
						<div className="flex items-baseline gap-2">
							<span className={`tnum text-[44px] leading-none font-semibold tracking-[-0.03em] ${inScenario ? "text-scenario-ink" : "text-peri-deeper"}`}>
								{s.curve[REPORT]}%
							</span>
							<span className="text-sm">chance by Dec 2027</span>
						</div>
						{inScenario && (
							<div className="border-l-2 border-baseline/50 pl-3 text-sm">
								<div className="text-xs text-quiet">Without scenario</div>
								<div className="tnum text-lg font-semibold text-ink">{base.curve[REPORT]}%</div>
							</div>
						)}
					</div>
					<Chart scenario={s} base={base} />
					<div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-quiet">
						{inScenario ? (
							<>
								<Legend color="var(--scenario)" label={s.name} />
								<Legend color="var(--baseline)" label="Without scenario" dashed />
							</>
						) : (
							<Legend color="var(--brand-periwinkle)" label="Model" />
						)}
					</div>
				</div>

				<div className="lg:col-span-5">
					<table className="w-full text-sm">
						<caption className="sr-only">Chance of cutting 10% or more of staff by Dec 2027, and the likely cut, in each scenario</caption>
						<thead>
							<tr className="border-b border-line text-left text-xs text-quiet">
								<th className="px-5 py-3 font-medium sm:px-6">Scenario</th>
								<th className="px-2 py-3 text-right font-medium whitespace-nowrap">By Dec 2027</th>
								<th className="py-3 pr-5 pl-2 text-right font-medium whitespace-nowrap sm:pr-6">Likely cut</th>
							</tr>
						</thead>
						<tbody>
							{SCENARIOS.map((x) => {
								const on = x.id === pick;
								const accent = x.id === "baseline" ? "var(--brand-periwinkle)" : "var(--scenario)";
								return (
									<tr
										key={x.id}
										className={`cursor-pointer border-b border-line-soft last:border-b-0 ${on ? (x.id === "baseline" ? "bg-peri-wash/70" : "bg-scenario-wash/70") : "hover:bg-[#faf8f9]"}`}
										onClick={() => setPick(x.id)}
									>
										<td className="relative px-5 py-3.5 sm:px-6">
											{on && <span className="absolute inset-y-0 left-0 w-[3px]" style={{ background: accent }} aria-hidden />}
											<button
												type="button"
												aria-pressed={on}
												onClick={(e) => {
													e.stopPropagation();
													setPick(x.id);
												}}
												className="text-left font-medium text-ink"
											>
												{x.name}
											</button>
											{x.how && <span className="mt-0.5 block text-xs text-quiet">{x.how}</span>}
										</td>
										<td className="tnum px-2 py-3.5 text-right font-medium text-ink">{x.curve[REPORT]}%</td>
										<td className="tnum py-3.5 pr-5 pl-2 text-right whitespace-nowrap sm:pr-6">{x.cut} people</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					<div className="border-t border-line px-5 py-4 text-xs text-quiet sm:px-6">
						<p>
							<span className="font-medium text-eggplant">What matters most:</span> Acme (68%), Initech following Acme (14%), Globex (12%), long-tail revenue
							(6%).
						</p>
						<p className="mt-2">Illustrative numbers from a worked example.</p>
					</div>
				</div>
			</div>
		</section>
	);
}

function Legend({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
	return (
		<span className="flex items-center gap-1.5">
			<svg width="18" height="6" aria-hidden>
				<line x1="0" x2="18" y1="3" y2="3" stroke={color} strokeWidth={2.5} strokeDasharray={dashed ? "3 3" : undefined} />
			</svg>
			{label}
		</span>
	);
}

function Chart({ scenario, base }: { scenario: Scenario; base: Scenario }) {
	const [ref, { w: W }] = useSize<HTMLDivElement>();
	const H = 240;
	const L = 4;
	const R = 40;
	const T = 12;
	const B = 26;
	const x = (m: number) => L + (m / SPAN) * (W - L - R);
	const y = (v: number) => T + (1 - v / 100) * (H - T - B);
	const pts = (c: number[]): Pt[] => c.map((v, i) => [x(AT[i]), y(v)]);
	const inScenario = scenario.id !== "baseline";
	const main = pts(scenario.curve);
	const area = `${smoothPath(main)}L${x(SPAN)} ${y(0)}L${x(0)} ${y(0)}Z`;
	const colour = inScenario ? "var(--scenario)" : "var(--brand-periwinkle)";
	// Chromium animates the CSS `d` property, so switching scenario morphs the curve; other browsers just redraw.
	const morph = (d: string) => ({ d: `path("${d}")`, transition: "d 450ms cubic-bezier(0.3,0,0.2,1)" }) as React.CSSProperties;

	return (
		<div ref={ref} className="mt-6">
			{W > 0 && (
				<svg width={W} height={H} role="img" aria-label={`${scenario.name}: ${scenario.curve[REPORT]}% chance by Dec 2027.`}>
					<g fontFamily="var(--font-mono)" fontSize={11} fill="var(--quiet)">
						{[25, 50, 75, 100].map((v) => (
							<g key={v}>
								<line x1={L} x2={x(SPAN)} y1={y(v)} y2={y(v)} stroke="var(--line-soft)" />
								<text x={x(SPAN) + 8} y={y(v) + 4}>
									{v}%
								</text>
							</g>
						))}
						<line x1={L} x2={x(SPAN)} y1={y(0)} y2={y(0)} stroke="var(--ink)" strokeOpacity={0.3} />
						{[
							[4, "2027"],
							[16, "2028"],
						].map(([m, label]) => (
							<text key={label} x={x(m as number)} y={H - 6} textAnchor="middle">
								{label}
							</text>
						))}
						<text x={x(0)} y={H - 6}>
							now
						</text>
						<line x1={x(15)} x2={x(15)} y1={T} y2={y(0)} stroke="var(--ink)" strokeOpacity={0.25} strokeDasharray="2 3" />
					</g>
					<path d={area} style={morph(area)} fill={colour} opacity={0.1} />
					{inScenario && <path d={smoothPath(pts(base.curve))} fill="none" stroke="var(--baseline)" strokeWidth={2} strokeDasharray="5 4" />}
					<path d={smoothPath(main)} style={{ ...morph(smoothPath(main)), stroke: colour }} fill="none" strokeWidth={3} strokeLinecap="round" />
					<circle
						cx={x(15)}
						cy={y(scenario.curve[REPORT])}
						r={5.5}
						fill="#fff"
						stroke={colour}
						strokeWidth={2.5}
						style={{ transition: "cy 450ms cubic-bezier(0.3,0,0.2,1)" }}
					/>
				</svg>
			)}
		</div>
	);
}
